import React, { useState } from 'react';
import querystring from 'query-string';

import CardDialog from '~/components/CardDialog';
import { LoadingSwitch, Text } from '~/components/core';
import useCurrentUserRoles from '~/components/hooks/useCurrentUserRoles';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import ConfigurationBreadcrumbs from './ConfigurationBreadcrumbs';
import ConfigurationFilters from './ConfigurationFilters';
import ConfigurationsTable from './ConfigurationsTable';
import { FILTERS_DEFAULT } from './constants';
import IncidentConfigurationDraftsList from './IncidentConfigurationDraftsList';
import type {
  ConfigurationScreenProps,
  Filters,
  IncidentConfigurationDraftSummary,
  IncidentConfigurationSummary,
  IncidentConfigurationSummaryList,
} from './types';

const paramsSerializer = (params: querystring.ParsedQuery<string | number | boolean | null | undefined>) =>
  querystring.stringify(params, { arrayFormat: 'none' });

type ConfigurationPageProps = {
  configurationTitle: string;
  configurationSubtitle: string;
  externalAdminRoute: string;
  ConfigurationScreen: React.FC<ConfigurationScreenProps>;
  searchPlaceholder: string;
  counterSuffix: string;
  displayName: string;
  trackMixpanelEvent: (incidentConfiguration: IncidentConfigurationSummary) => void;
  displayFNOLInboxCols?: boolean;
  displayPolicyCliveConfig?: boolean;
  displayPostFnolHook?: boolean;
  isInternalAdmin?: boolean;
  // Policy-page only: a per-configuration categories management screen, swapped in like
  // ConfigurationScreen. The row's Actions menu shows "Edit Categories" only when this is
  // provided.
  CategoriesScreen?: React.FC<{ configurationSummary: IncidentConfigurationSummary; onBack: () => void }>;
  // Page-level settings that apply to every configuration in the list, rendered under the search box.
  headerSlot?: React.ReactNode;
};

const ConfigurationPage: React.FC<ConfigurationPageProps> = ({
  configurationTitle,
  configurationSubtitle,
  externalAdminRoute,
  ConfigurationScreen,
  searchPlaceholder,
  counterSuffix,
  displayName,
  trackMixpanelEvent,
  displayFNOLInboxCols = false,
  displayPolicyCliveConfig = false,
  displayPostFnolHook = false,
  isInternalAdmin = false,
  CategoriesScreen,
  headerSlot,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { isUserFromSuperOrganization } = useCurrentUserRoles() as { isUserFromSuperOrganization?: boolean };
  const [incidentConfigIdToEdit, setIncidentConfigIdToEdit] = useState<number | null>(null);
  const [draftToEdit, setDraftToEdit] = useState<IncidentConfigurationDraftSummary | null>(null);
  const [categoriesConfigSummary, setCategoriesConfigSummary] = useState<IncidentConfigurationSummary | null>(null);

  const showEditCategories = !!CategoriesScreen;

  // The generic drafts list (and the draft-edit flow) is internal-admin only, gated on the
  // logged-in operator's super-org membership and the drafts feature flag - customers never see it.
  const showDraftsUi =
    isInternalAdmin &&
    isUserFromSuperOrganization &&
    isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.INCIDENT_CONFIGURATION_DRAFTS);

  const [filters, setFilters] = useState<Filters>({ ...FILTERS_DEFAULT });

  const { searchText, selectedLob, subOrganizationIds } = filters;

  const baseIncidentConfigurationsRoute = `/sysconfig/api/v1/organizations/${organization.id}/incident_configurations`;
  const {
    isLoading,
    isError,
    data: incidentConfigSummaries,
    reloadData: reloadIncidentConfigSummaries,
  } = useDataFetcher<IncidentConfigurationSummaryList>(`${baseIncidentConfigurationsRoute}/summary`, {
    // The count is an extra aggregate on the server, so it is asked for only by the page
    // that renders it: the FNOL list shares this route and hides the column, and
    // showEditCategories is the same signal that decides whether the column is shown.
    params: {
      lob: selectedLob,
      sub_organization_ids: subOrganizationIds,
      include_policy_categories_count: showEditCategories,
    },
    paramsSerializer,
  });

  const filteredIncidentConfigSummaries = (incidentConfigSummaries || []).filter((ic: { display_name: string }) =>
    ic.display_name.toLowerCase().includes(searchText.toLowerCase())
  );

  // A draft carries its own configuration/sub-org context, so when editing a draft prefer it
  // over the summary lookup (the owning configuration may be filtered out of the visible list).
  const subOrgIdToEdit = draftToEdit
    ? draftToEdit.sub_organization_id
    : incidentConfigSummaries?.find((ic: { id: number }) => ic.id === incidentConfigIdToEdit)?.sub_organization_id;

  const hasActiveInbox = React.useMemo(() => {
    if (!incidentConfigIdToEdit) return false;
    // With sub-orgs several summaries share the same incident_configuration_id, so match on the
    // sub-organization too - keying on id alone can follow the wrong sub-org's inbox when editing a draft.
    const selectedConfig = incidentConfigSummaries?.find(
      (ic: IncidentConfigurationSummary) =>
        ic.id === incidentConfigIdToEdit && (ic.sub_organization_id ?? null) === (subOrgIdToEdit ?? null)
    );
    return !!selectedConfig?.fnol_email_inbox_name && selectedConfig?.should_send_fnol_email_inbox_to_clive === true;
  }, [incidentConfigSummaries, incidentConfigIdToEdit, subOrgIdToEdit]);

  const handleOnCancel = () => {
    setIncidentConfigIdToEdit(null);
    setDraftToEdit(null);
  };

  const handleOnSubmit = async () => {
    await reloadIncidentConfigSummaries();
    handleOnCancel();
  };

  const handleEditDraft = (draft: IncidentConfigurationDraftSummary) => {
    setDraftToEdit(draft);
    setIncidentConfigIdToEdit(draft.incident_configuration_id);
  };

  if (categoriesConfigSummary && CategoriesScreen) {
    return (
      <CategoriesScreen
        configurationSummary={categoriesConfigSummary}
        // Reloaded on the way back, the same shape as handleOnSubmit: the list's Categories
        // count is stale the moment a category is added or deleted on that screen.
        onBack={async () => {
          await reloadIncidentConfigSummaries();
          setCategoriesConfigSummary(null);
        }}
      />
    );
  }

  return (
    <>
      {!incidentConfigIdToEdit && (
        <div className="p-12">
          <ConfigurationBreadcrumbs
            externalAdminRoute={externalAdminRoute}
            configurationName={configurationTitle}
            onCancel={handleOnCancel}
          />
        </div>
      )}
      {!incidentConfigIdToEdit ? (
        <CardDialog title={configurationTitle}>
          <Text variant="small" weight="regular">
            {configurationSubtitle}
          </Text>
          <ConfigurationFilters
            filters={filters}
            setFilters={setFilters}
            searchPlaceholder={searchPlaceholder}
            headerSlot={headerSlot}
          />
          <LoadingSwitch isLoading={isLoading} isError={isError}>
            <ConfigurationsTable
              incidentConfigSummaries={filteredIncidentConfigSummaries}
              onEditIncidentConfig={setIncidentConfigIdToEdit}
              counterSuffix={counterSuffix}
              displayName={displayName}
              trackMixpanelEvent={trackMixpanelEvent}
              reloadIncidentConfigSummaries={reloadIncidentConfigSummaries}
              displayFNOLInboxCols={displayFNOLInboxCols}
              displayPolicyCliveConfig={displayPolicyCliveConfig}
              displayPostFnolHook={displayPostFnolHook}
              onEditCategories={showEditCategories ? setCategoriesConfigSummary : undefined}
            />
            {showDraftsUi && (
              <IncidentConfigurationDraftsList displayName={displayName} onEditDraft={handleEditDraft} />
            )}
          </LoadingSwitch>
        </CardDialog>
      ) : (
        <ConfigurationScreen
          incidentConfigurationId={incidentConfigIdToEdit}
          subOrgId={subOrgIdToEdit}
          draftId={draftToEdit?.id ?? null}
          hasActiveInbox={hasActiveInbox}
          isInternalAdmin={isInternalAdmin}
          onCancel={handleOnCancel}
          onSubmit={handleOnSubmit}
        />
      )}
    </>
  );
};

const InternalConfiguration: React.FC<ConfigurationPageProps> = ({
  configurationTitle,
  configurationSubtitle,
  externalAdminRoute,
  ConfigurationScreen,
  searchPlaceholder,
  counterSuffix,
  displayName,
  trackMixpanelEvent,
  displayFNOLInboxCols,
  displayPolicyCliveConfig,
  displayPostFnolHook,
  isInternalAdmin,
  CategoriesScreen,
  headerSlot,
}) => (
  <div className="absolute inset-0 overflow-scroll">
    <ConfigurationPage
      configurationTitle={configurationTitle}
      configurationSubtitle={configurationSubtitle}
      externalAdminRoute={externalAdminRoute}
      ConfigurationScreen={ConfigurationScreen}
      searchPlaceholder={searchPlaceholder}
      counterSuffix={counterSuffix}
      displayName={displayName}
      trackMixpanelEvent={trackMixpanelEvent}
      displayFNOLInboxCols={displayFNOLInboxCols}
      displayPolicyCliveConfig={displayPolicyCliveConfig}
      displayPostFnolHook={displayPostFnolHook}
      isInternalAdmin={isInternalAdmin}
      CategoriesScreen={CategoriesScreen}
      headerSlot={headerSlot}
    />
  </div>
);

export { InternalConfiguration };
export default ConfigurationPage;
